// Admin-only "Test Ads" card — appears at the bottom of the Profile screen
// for users with the `admin` role. Flips Google's official test ad units on
// for the admin's session so every placement can be verified on a real
// device before the production AdMob IDs are wired up.
import { useEffect, useState } from "react";
import { Megaphone, PlayCircle, Image as ImageIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import {
  isAdminTestAds, setAdminTestAds, subscribeAdminTestAds,
} from "@/lib/admin-test-ads";
import {
  showInterstitial, showRewardedAd, showBanner, hideBanner,
} from "@/lib/native-ads";
import { isAndroidApp } from "@/lib/platform";

export const AdminTestAdsCard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [on, setOn] = useState<boolean>(isAdminTestAds());
  const [busy, setBusy] = useState<null | "interstitial" | "rewarded" | "banner">(null);

  useEffect(() => subscribeAdminTestAds(setOn), []);

  const toggle = (next: boolean) => {
    setAdminTestAds(next);
    toast({
      title: next ? "Admin Test Ads ON" : "Admin Test Ads OFF",
      description: next
        ? "Google test ads will appear for your account on every placement."
        : "Reverted to normal ad behaviour for your account.",
    });
  };

  const native = isAndroidApp();

  const tryInterstitial = async () => {
    setBusy("interstitial");
    try { await showInterstitial(); } finally { setBusy(null); }
  };
  const tryRewarded = async () => {
    setBusy("rewarded");
    try {
      const r = await showRewardedAd({ userId: user?.id });
      toast({
        title: r.rewarded ? "Rewarded ✓" : "Ad closed without reward",
        description: `Unit: ${r.adUnitId}`,
      });
    } finally { setBusy(null); }
  };
  const tryBanner = async () => {
    setBusy("banner");
    try {
      await showBanner();
      setTimeout(() => { void hideBanner(); }, 8000);
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500">
          <Megaphone className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Admin: Test Ads</p>
          <p className="text-xs text-muted-foreground">
            Force Google test ads on every placement for your account.
          </p>
        </div>
        <Switch checked={on} onCheckedChange={toggle} />
      </div>

      {on && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <TestButton
            label="Interstitial"
            icon={<PlayCircle className="h-4 w-4" />}
            disabled={!native || busy !== null}
            loading={busy === "interstitial"}
            onClick={tryInterstitial}
          />
          <TestButton
            label="Rewarded"
            icon={<PlayCircle className="h-4 w-4" />}
            disabled={!native || busy !== null}
            loading={busy === "rewarded"}
            onClick={tryRewarded}
          />
          <TestButton
            label="Banner"
            icon={<ImageIcon className="h-4 w-4" />}
            disabled={!native || busy !== null}
            loading={busy === "banner"}
            onClick={tryBanner}
          />
        </div>
      )}

      {on && !native && (
        <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-300">
          Buttons fire only inside the installed Android app — the web preview
          can't render AdMob units.
        </p>
      )}
    </div>
  );
};

const TestButton = ({
  label, icon, disabled, loading, onClick,
}: {
  label: string;
  icon: React.ReactNode;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-medium hover:border-amber-500/40 hover:bg-amber-500/10 disabled:opacity-40"
  >
    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
    {label}
  </button>
);

export default AdminTestAdsCard;
